/**
 * REQ-011 error mapping. Pure function so it is unit-testable in isolation.
 * @MX:ANCHOR: SPEC-011 REQ-011 single source of truth for login HTTP status -> Russian user-facing message.
 * @MX:REASON: Consumed by login page (UI) and onSubmit handler tests; centralizing prevents drift.
 */
export type LoginErrorKind =
  | "phone_format"
  | "invalid_credentials"
  | "csrf_retry_failed"
  | "rate_limited"
  | "network_or_5xx"
  | "unknown";

export interface LoginErrorMappingInput {
  status?: number;
  detail?: string;
  networkFailure?: boolean;
  // SPEC-092 D15: секунды из заголовка Retry-After при 429 (ограничитель входа).
  retryAfter?: number;
}

export interface LoginErrorMappingResult {
  kind: LoginErrorKind;
  message: string;
}

export function mapLoginError(
  input: LoginErrorMappingInput,
): LoginErrorMappingResult {
  if (input.networkFailure) {
    return {
      kind: "network_or_5xx",
      message: "Сервис недоступен, попробуйте позже",
    };
  }
  const s = input.status;
  if (s === 400) {
    return { kind: "phone_format", message: "Неверный формат телефона" };
  }
  if (s === 401) {
    // @MX:NOTE: Security M-2 (SPEC-011 audit) — message is hard-coded, NOT taken from
    // backend `detail`. Backend uses unified `GENERIC_LOGIN_ERROR` to prevent user
    // enumeration via timing/wording; mirroring that here preserves the defence even if
    // a future backend regression leaks a non-generic detail.
    return {
      kind: "invalid_credentials",
      message: "Неверный телефон или пароль",
    };
  }
  if (s === 403) {
    return {
      kind: "csrf_retry_failed",
      message: "Ошибка безопасности, обновите страницу",
    };
  }
  if (s === 429) {
    // SPEC-092 D15: ограничитель попыток входа (3 неудачи → 429). Сколько ждать,
    // говорит сервер заголовком Retry-After — вызывающий кладёт его в retryAfter.
    const wait = input.retryAfter;
    return {
      kind: "rate_limited",
      message:
        wait !== undefined && Number.isFinite(wait) && wait > 0
          ? `Слишком много попыток входа, попробуйте через ${wait} секунд`
          : "Слишком много попыток входа, попробуйте позже",
    };
  }
  if (s !== undefined && s >= 500) {
    return {
      kind: "network_or_5xx",
      message: "Сервис недоступен, попробуйте позже",
    };
  }
  return { kind: "unknown", message: "Неизвестная ошибка" };
}
